import type {
  IPricingPlan,
  IProductInfo,
  IProductInfoAnnouncement,
  IProductInfoBanner,
  IProductInfoCapabilities,
  IProductInfoCapabilityItem,
  IProductInfoLearnMore,
  IProductInfoLearnMoreItem,
  IProductInfoOverview,
  IProductInfoOverviewFeature,
  IProductResources,
  IResource,
} from '@/assets/types/api/Products';
import { EnumBillingCycle } from '@/assets/types/api/Products';

enum EnumProductsLeadType {
  BOOK_A_DEMO = 'book-a-demo',
  CONTACT_SALES = 'contact-sales',
}

// 表單

interface IForm {
  message: string;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: number;
}

// 表單錯誤訊息管理

interface IFormErrorMessage {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: string;
}

const getForm = (): IForm => ({
  message: '',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: 0,
});

const getFormErrorMessage = (): IFormErrorMessage => ({
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: '',
});

export const useProductsStore = defineStore('productsStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const detail = ref<IProductInfo>({
    id: null,
    agreementId: null,
    banner: {
      name: '',
      description: '',
      webBanner: null,
      tabletBanner: null,
      mobileBanner: null,
      isFreeTrial: false,
    },
    announcement: null,
    overview: {
      sectionTitle: '',
      mediaType: 1,
      mediaWebThumbnail: '',
      mediaTabletThumbnail: '',
      mediaMobileThumbnail: '',
      videoUrl: null,
      features: [],
    },
    capabilities: {
      sectionTitle: '',
      items: [],
    },
    learnMore: {
      sectionTitle: '',
      recommendProducts: [],
    },
  });

  const resources = ref<IProductResources>({
    videos: [],
    catalogs: [],
    industryFocuses: [],
    whitePapers: [],
    caseStudies: [],
    news: [],
  });

  const form = ref<IForm>(getForm());
  const formErrorMessage = ref<IFormErrorMessage>(getFormErrorMessage());
  const isShowResult = ref<boolean>(false);
  const isSubmitting = ref<boolean>(false);
  const isShowMobileMenu = ref<boolean>(false);

  /* Getters */

  const productId = computed((): number | null => {
    return detail.value.id;
  });

  const productName = computed((): string => {
    return detail.value.banner.name;
  });

  const productBanner = computed((): IProductInfoBanner => {
    return detail.value.banner;
  });

  const productAnnouncement = computed((): IProductInfoAnnouncement | null => {
    return detail.value.announcement;
  });

  const productOverview = computed((): IProductInfoOverview => {
    return detail.value.overview;
  });

  const productFeatures = computed((): IProductInfoOverviewFeature[] => {
    return detail.value.overview.features;
  });

  const productCapabilities = computed((): IProductInfoCapabilities => {
    return detail.value.capabilities;
  });

  const productCapabilityItems = computed((): IProductInfoCapabilityItem[] => {
    return detail.value.capabilities.items;
  });

  const productLearnMore = computed((): IProductInfoLearnMore => {
    return detail.value.learnMore;
  });

  const productRecommendProducts = computed((): IProductInfoLearnMoreItem[] => {
    return detail.value.learnMore.recommendProducts;
  });

  const productCases = computed((): IResource[] => {
    return resources.value.caseStudies;
  });

  const isShowSectionAnnouncement = computed((): boolean => {
    return productAnnouncement.value !== null;
  });

  const isShowSectionCapabilities = computed((): boolean => {
    // 判斷是否顯示 Capabilities 區塊
    return productCapabilityItems.value.length > 0;
  });

  const isShowSectionCases = computed((): boolean => {
    // 判斷是否顯示 Cases 區塊
    return productCases.value.length > 0;
  });

  const isShowSectionLearnMore = computed((): boolean => {
    // 判斷是否顯示 Learn More 區塊
    return productRecommendProducts.value.length > 0;
  });

  const hasPricingPlan = computed((): boolean => {
    /**
     * 判斷產品是否有 Plan
     * 呼叫 [GET] Pricing Plans 判斷至少有一個 serviceType 即代表有 Plan
     */
    const pricingStore = usePricingStore();
    const { pricing } = storeToRefs(pricingStore);
    return pricing.value.serviceType.length > 0;
  });

  const freeTrialPlan = computed((): IPricingPlan | null => {
    /**
     * FreeTrial Plan 的資訊（如果產品有包含 Free Trial 的話）
     */

    const pricingStore = usePricingStore();
    const { pricing } = storeToRefs(pricingStore);

    return pricing.value.serviceType.reduce(
      (prev, current) => {
        if (prev === null) {
          const result = current.plans.find(
            (plan) => plan.billingCycleId === EnumBillingCycle.FREE_TRIAL
          );
          return result || null;
        }
        return prev;
      },
      null as IPricingPlan | null
    );
  });

  const hasFreeTrialPlan = computed((): boolean => {
    /**
     * 判斷是否有 Free Trial Plan
     */
    if (freeTrialPlan.value === null) {
      return false;
    }
    return true;
  });

  /* Actions */

  const USE_GET_PRODUCT_DETAIL = async () => {
    /**
     * 取得指定產品的詳細資訊
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    const id = computed((): string => {
      return !Array.isArray(route.params?.id) ? route.params.id || '' : '';
    });

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/products/${id.value}?websiteCode=${locale.value}`,
      {
        key: `USE_GET_PRODUCT_DETAIL_${id.value}`,
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            Object.assign(detail.value, _data);
          } else {
            console.error(_data);
          }
        },
      }
    );
  };

  const USE_GET_PRODUCT_RESOURCES = async () => {
    /**
     * 取得產品資源資訊
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    const id = computed((): string => {
      return !Array.isArray(route.params?.id) ? route.params.id || '' : '';
    });

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/products/resources/${id.value}?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            Object.assign(resources.value, _data);
          } else {
            console.error(_data);
          }
        },
      }
    );
  };

  const POST_PRODUCTS_LEADS = async (
    leadTypeCode: EnumProductsLeadType,
    formData: FormData
  ): Promise<void> => {
    /**
     * 送出產品 Leads 收集相關表單
     */
    const { $customFetch, $notification, $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    try {
      await $customFetch(
        `/marketplace/api/v1/leads/products/${leadTypeCode}/${route.params.id}?websiteCode=${locale.value}`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
      return Promise.reject(error);
    }
  };

  const FILL_FORM_WITH_PROFILE = (): void => {
    /**
     * 用戶已登入時表單自動代入資料
     */

    const profileStore = useProfileStore();
    const { profile } = storeToRefs(profileStore);

    if (!profile.value.isLogin) return;

    form.value.firstName = profile.value.firstName ?? '';
    form.value.lastName = profile.value.lastName ?? '';
    form.value.email = profile.value.email ?? '';
    form.value.phoneCode = profile.value.mobileCountryCode ?? '';
    form.value.phoneNumber = profile.value.mobileNumber ?? '';
    form.value.companyName = profile.value.companyName ?? '';
    form.value.country = profile.value.country ?? '';
  };

  const VALID_FORM_FIRST_NAME = (): boolean => {
    /**
     * 驗證 firstName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.firstName === '') {
      formErrorMessage.value.firstName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.firstName = '';
    return true;
  };

  const VALID_FORM_LAST_NAME = (): boolean => {
    /**
     * 驗證 lastName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.lastName === '') {
      formErrorMessage.value.lastName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.lastName = '';
    return true;
  };

  const VALID_FORM_COMPANY_NAME = (): boolean => {
    /**
     * 驗證 companyName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.companyName === '') {
      formErrorMessage.value.companyName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.companyName = '';
    return true;
  };

  const VALID_FORM_EMAIL = (): boolean => {
    /**
     * 驗證 email 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.email === '') {
      formErrorMessage.value.email = t('form.error-required-message');
      return false;
    }

    if (!validateCompanyEmail(form.value.email)) {
      formErrorMessage.value.email = t('form.error-email-message');
      return false;
    }

    formErrorMessage.value.email = '';
    return true;
  };

  const VALID_FORM_PHONE_CODE = (): boolean => {
    /**
     * 驗證 phoneCode 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneCode === '') {
      formErrorMessage.value.phoneCode = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.phoneCode = '';
    return true;
  };

  const VALID_FORM_PHONE_NUMBER = (): boolean => {
    /**
     * 驗證 phoneNumber 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneNumber === '') {
      formErrorMessage.value.phoneNumber = t('form.error-required-message');
      return false;
    }

    const isNumeric = /^\d+$/.test(form.value.phoneNumber);
    if (!isNumeric) {
      formErrorMessage.value.phoneNumber = t('form.error-numeric-only');
      return false;
    }

    formErrorMessage.value.phoneNumber = '';
    return true;
  };

  const VALID_FORM_COUNTRY = (): boolean => {
    /**
     * 驗證 country 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.country === '') {
      formErrorMessage.value.country = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.country = '';
    return true;
  };

  const VALID_FORM_EMPLOYEES_RANGE = (): boolean => {
    /**
     * 驗證 employeeRange 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    const mainStore = useMainStore();
    const { employeeRanges } = storeToRefs(mainStore);

    if (form.value.employeeRange === 0) {
      formErrorMessage.value.employeeRange = t('form.error-required-message');
      return false;
    }

    const isValidRange = employeeRanges.value.some(
      (range) => range.id === form.value.employeeRange
    );

    if (!isValidRange) {
      formErrorMessage.value.employeeRange = t('form.error-invalid-selection');
      return false;
    }

    formErrorMessage.value.employeeRange = '';
    return true;
  };

  const VALID_FORM = (): boolean => {
    /**
     * 驗證所有欄位（驗證通過回傳 True）
     */

    const result = [
      VALID_FORM_FIRST_NAME(),
      VALID_FORM_LAST_NAME(),
      VALID_FORM_COMPANY_NAME(),
      VALID_FORM_EMAIL(),
      VALID_FORM_PHONE_CODE(),
      VALID_FORM_PHONE_NUMBER(),
      VALID_FORM_COUNTRY(),
      VALID_FORM_EMPLOYEES_RANGE(),
    ];

    return result.every((value) => value === true);
  };

  const RESET_FORM = (): void => {
    /**
     * 重置表單
     */

    form.value = getForm();
    formErrorMessage.value = getFormErrorMessage();
  };

  return {
    detail,
    resources,
    form,
    formErrorMessage,
    isShowResult,
    isSubmitting,
    isShowMobileMenu,
    productId,
    productName,
    productBanner,
    productAnnouncement,
    productOverview,
    productFeatures,
    productCapabilities,
    productCapabilityItems,
    productLearnMore,
    productRecommendProducts,
    productCases,
    isShowSectionAnnouncement,
    isShowSectionCapabilities,
    isShowSectionCases,
    isShowSectionLearnMore,
    hasPricingPlan,
    hasFreeTrialPlan,
    freeTrialPlan,
    USE_GET_PRODUCT_DETAIL,
    USE_GET_PRODUCT_RESOURCES,
    POST_PRODUCTS_LEADS,
    FILL_FORM_WITH_PROFILE,
    VALID_FORM_FIRST_NAME,
    VALID_FORM_LAST_NAME,
    VALID_FORM_COMPANY_NAME,
    VALID_FORM_EMAIL,
    VALID_FORM_PHONE_CODE,
    VALID_FORM_PHONE_NUMBER,
    VALID_FORM_COUNTRY,
    VALID_FORM_EMPLOYEES_RANGE,
    VALID_FORM,
    RESET_FORM,
  };
});

export { EnumProductsLeadType };
